import { LIBRARY_REFERENCE_ALIASES } from '@/data/library/library-reference-aliases';
import { ARCHIVED_EXERCISE_MATERIALS } from '@/data/course-materials/archived-exercise-materials';

export interface LibraryExerciseConfiguration {
  sets: number;
  reps: string;
  restSeconds: number;
}

const legacyWorkloads = new Map(ARCHIVED_EXERCISE_MATERIALS.map(exercise => [exercise.id, {
  sets: exercise.sets, reps: exercise.reps, restSeconds: exercise.restSeconds,
}]));

/** Slots retain distinct execution/configuration identities while sharing one standard movement. */
export function getLibraryReferenceBaseId(id: string): string {
  if (!id.startsWith('library-')) return id;
  const base = id.replace(/::slot-\d+-\d+$/, '');
  const aliases = LIBRARY_REFERENCE_ALIASES[base.slice('library-'.length)];
  // An earlier standard may later merge; keep its saved references and slots usable.
  return aliases?.length === 1 ? aliases[0] : base;
}

export function getLibraryReferenceIds(id: string): string[] {
  const base = getLibraryReferenceBaseId(id);
  return LIBRARY_REFERENCE_ALIASES[base] || [base];
}

/** A checkbox selection is a set. Training sequences below are deliberately not sets. */
export function normalizeLibrarySelectionIds(ids: string[]): string[] {
  return [...new Set(ids.filter(id => typeof id === 'string').flatMap(getLibraryReferenceIds))];
}

export function normalizeLibraryPlanReferences(
  ids: string[],
  configs?: Record<string, LibraryExerciseConfiguration>,
) {
  const exerciseIds: string[] = [];
  const exerciseConfigs: Record<string, LibraryExerciseConfiguration> = { ...configs };
  const used = new Set<string>();
  for (const [index, oldId] of ids.entries()) {
    for (const [part, base] of getLibraryReferenceIds(oldId).entries()) {
      const id = used.has(base) && base.startsWith('library-') ? `${base}::slot-${index}-${part}` : base;
      used.add(base);
      exerciseIds.push(id);
      const config = configs?.[oldId] || configs?.[getLibraryReferenceBaseId(oldId)] ||
        (LIBRARY_REFERENCE_ALIASES[oldId] ? legacyWorkloads.get(oldId) : undefined);
      if (config) exerciseConfigs[id] = { ...config };
    }
  }
  return { exerciseIds, exerciseConfigs: Object.keys(exerciseConfigs).length ? exerciseConfigs :
    configs ? exerciseConfigs : undefined };
}

/** Apply on read; a library deployment does not rewrite stored plans or historical workouts. */
export function normalizeLibraryCustomPlan<T extends {
  exerciseIds: string[];
  exerciseConfigs?: Record<string, LibraryExerciseConfiguration>;
}>(plan: T): T {
  return { ...plan, ...normalizeLibraryPlanReferences(plan.exerciseIds, plan.exerciseConfigs) };
}

export function normalizeFreeLibraryDay<T extends {
  exerciseIds?: string[];
  exerciseSettings?: Record<string, LibraryExerciseConfiguration>;
}>(day: T): T {
  const mapped = normalizeLibraryPlanReferences(day.exerciseIds || [], day.exerciseSettings);
  return { ...day, exerciseIds: mapped.exerciseIds, exerciseSettings: mapped.exerciseConfigs };
}
